import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CACHE_KEY = "bja_leads";

/**
 * Single source of truth for lead data across CRM / Today / Inbox.
 * Polls /api/demo/leads every `pollMs` (default 30s) and exposes
 * { leads, loading, lastSyncedAt, error, refresh }.
 *
 * Falls back to localStorage cache only on first paint to avoid empty UI.
 */
export default function useLiveLeads({ pollMs = 30000, enabled = true } = {}) {
  const [leads, setLeads] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY)) || [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState(null);
  const failureCount = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/demo/leads`, { cache: "no-store" });
      if (!r.ok) throw new Error(`API ${r.status}`);
      const data = await r.json();
      const fresh = data.leads || [];
      setLeads(fresh);
      setLastSyncedAt(new Date());
      setError(null);
      failureCount.current = 0;
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(fresh)); } catch { /* quota */ }
    } catch (e) {
      failureCount.current += 1;
      if (failureCount.current >= 3) setError(e.message || "Sync failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [enabled, pollMs, refresh]);

  return { leads, loading, lastSyncedAt, error, refresh, failureCount: failureCount.current };
}
